<template>
  <div 
    class="form-drop-area"
    :class="{ 'drag-over': isDragOver && formElements.length === 0 }"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <div v-if="formElements.length === 0" class="empty-state">
      <p class="empty-title">Your form is empty.</p>
      <!-- Original:
      <p class="empty-description">
        Drag elements from the panel or use the 
        <a href="#" class="ai-link" @click.prevent="$emit('open-ai')">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 1.33334L9.53 5.43334H13.87L10.33 8.04001L11.87 12.1333L8 9.52668L4.13 12.1333L5.67 8.04001L2.13 5.43334H6.47L8 1.33334Z" fill="currentColor"/>
          </svg>
          AI Assistant
        </a> .
      </p>
      -->
      <div class="empty-description">
        <span>Drag elements from the panel or use the</span>
        <ResponsiveButton
          display_name="AI Assistant"
          button_type="text"
          @click-button="$emit('open-ai')"
        >
          <template #prefix>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 1.33334L9.53 5.43334H13.87L10.33 8.04001L11.87 12.1333L8 9.52668L4.13 12.1333L5.67 8.04001L2.13 5.43334H6.47L8 1.33334Z" fill="currentColor"/>
            </svg>
          </template>
        </ResponsiveButton>
        <span>.</span>
      </div>
    </div>
    <div v-else class="form-elements-list">
      <!-- Drop zone before first element -->
      <div
        class="drop-zone"
        :class="{ 'drop-zone--active': dropTargetIndex === 0 }"
        @dragover.prevent="handleDropZoneDragOver($event, 0)"
        @dragleave="handleDropZoneDragLeave"
        @drop.prevent="handleDropZoneDrop($event, 0)"
      >
        <div class="drop-indicator"></div>
      </div>

      <template v-for="(element, index) in formElements" :key="element.id">
        <div
          class="form-element-wrapper"
          :class="{ 'is-dragging': draggingId === element.id }"
          :draggable="!disabled && !isElementEditing(element.id)"
          @dragstart="handleElementDragStart($event, index)"
          @dragend="handleElementDragEnd"
        >
            <div class="element-preview">
              <component
                :ref = "element.id"
                :is="getPreviewComponent(element.type)"
                :element="element"
                :disabled="disabled"
                @delete="removeElement(index)"
                @element-type-changed="handleElementTypeChanged(index, $event)"
                @update-element-data="handleElementDataUpdate(index, $event)"
                @element-selected="handleElementSelected"
                @element-deselected="handleElementDeselected"
                @auto-mapping = "handleAutoMapping"
                @element-edit-state-changed="handleElementEditStateChanged"
              />
            </div>
        </div>

        <!-- Drop zone after each element -->
        <div
          class="drop-zone"
          :class="{ 'drop-zone--active': dropTargetIndex === index + 1 }"
          @dragover.prevent="handleDropZoneDragOver($event, index + 1)"
          @dragleave="handleDropZoneDragLeave"
          @drop.prevent="handleDropZoneDrop($event, index + 1)"
        >
          <div class="drop-indicator"></div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  SectionHeaderPreview,
  ClinicBrandingPreview,
  NoteHelpPreview,
  ImagePreview,
  ShortResponsePreview,
  LongResponsePreview,
  DescriptionPreview,
  EmailPreview,
  PhonePreview,
  SingleSelectionPreview,
  MultiSelectionPreview,
  DropdownPreview,
  CheckboxPreview,
  YesNoPreview,
  DatePreview,
  DateTimePreview,
  TimePreview,
  NumericPreview,
  RatingPreview,
  SignerPreview,
  DefaultPreview,
  previewComponentMap
} from './previews';
import { mapGetters } from 'vuex';

export default {
  name: 'FormDropArea',
  components: {
    SectionHeaderPreview,
    ClinicBrandingPreview,
    NoteHelpPreview,
    ImagePreview,
    ShortResponsePreview,
    LongResponsePreview,
    DescriptionPreview,
    EmailPreview,
    PhonePreview,
    SingleSelectionPreview,
    MultiSelectionPreview,
    DropdownPreview,
    CheckboxPreview,
    YesNoPreview,
    DatePreview,
    DateTimePreview,
    TimePreview,
    NumericPreview,
    RatingPreview,
    SignerPreview,
    DefaultPreview
  },
  props: {
    formElements: {
      type: Array,
      default: () => []
    },
    selectedId: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'element-dropped',
    'element-removed',
    'element-type-changed',
    'element-reordered',
    'element-data-updated',
    'element-selected',
    'element-deselected',
    'open-ai',
    'auto-mapping',
    'element-edit-state-changed'
  ],
  data() {
    return {
      isDragOver: false,
      dropTargetIndex: null,
      draggingId: null, // id of the element currently being dragged within the form
      draggingSourceIndex: null // index of the element being reordered
    };
  },
  computed: {
    ...mapGetters('workbench/formBuilder', ['isElementEditing'])
    
  },
  watch: {
    selectedId(newVal, oldVal) {
      // console.log('selectedId changed from drop area', newVal, oldVal);
      if (newVal) {
        if (this.$refs[newVal]?.[0]?.$data && this.$refs[newVal][0].$data.isSelected !== true) {
          this.$refs[newVal][0].$data.isSelected = true;
        }
        //auto scroll to this element
        this.$refs[newVal]?.[0].$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } 
      if (oldVal && this.$refs[oldVal]?.[0]?.$data?.isSelected === true) {
        this.$refs[oldVal][0].$data.isSelected = false;
      }
    }
  },
  methods: {
    // ── Main area drag events (only for empty state) ──────────
    handleDragOver(event) {
      if (this.formElements.length === 0) {
        this.isDragOver = true;
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    handleDragLeave() {
      this.isDragOver = false;
    },
    handleDrop(event) {
      // Only accept direct drops when form is empty
      if (this.formElements.length > 0) return;
      this.isDragOver = false;
      this.dropTargetIndex = null;
      this.parseAndEmitNewElement(event, 0);
    },

    // ── Drop zone events (between / before / after elements) ──
    handleDropZoneDragOver(event, index) {
      event.stopPropagation();
      this.isDragOver = false;

      // Prevent dropping adjacent to its own position (no-op reorder)
      if (this.draggingSourceIndex !== null) {
        if (index === this.draggingSourceIndex || index === this.draggingSourceIndex + 1) {
          event.dataTransfer.dropEffect = 'none';
          this.dropTargetIndex = null;
          return;
        }
        event.dataTransfer.dropEffect = 'move';
      } else {
        event.dataTransfer.dropEffect = 'copy';
      }
      this.dropTargetIndex = index;
    },
    handleDropZoneDragLeave() {
      this.dropTargetIndex = null;
    },
    handleDropZoneDrop(event, index) {
      event.stopPropagation();
      const targetIndex = index;
      this.dropTargetIndex = null;
      this.isDragOver = false;

      // If we are reordering an existing element
      if (this.draggingSourceIndex !== null) {
        const fromIndex = this.draggingSourceIndex;
        // Adjust target when moving downward
        const toIndex = targetIndex > fromIndex ? targetIndex - 1 : targetIndex;
        if (fromIndex !== toIndex) {
          this.$emit('element-reordered', { fromIndex, toIndex });
        }
        this.draggingSourceIndex = null;
        this.draggingId = null;
        return;
      }

      // Otherwise it's a new element from the left panel
      this.parseAndEmitNewElement(event, targetIndex);
    },

    // ── Existing-element drag (reorder) ──────────────────────
    handleElementDragStart(event, index) {
      this.draggingSourceIndex = index;
      this.draggingId = this.formElements[index].id;
      event.dataTransfer.effectAllowed = 'move';
      // Set data so the browser allows the drag
      event.dataTransfer.setData('application/form-reorder', JSON.stringify({ sourceIndex: index }));
    },
    handleElementDragEnd() {
      this.draggingSourceIndex = null;
      this.draggingId = null;
      this.dropTargetIndex = null;
    },

    // ── Helpers ──────────────────────────────────────────────
    parseAndEmitNewElement(event, index) {
      try {
        const elementData = JSON.parse(event.dataTransfer.getData('application/json'));
        this.$emit('element-dropped', { element: elementData, index });
      } catch (e) {
        console.error('Failed to parse dropped element data:', e);
      }
    },
    removeElement(index) {
      this.$emit('element-removed', index);
    },
    handleElementEditStateChanged({ id, isEditing }) {
      this.$emit('element-edit-state-changed', { id, isEditing });
    },
    handleElementTypeChanged(index, newElement) {
      this.$emit('element-type-changed', { index, newElement });
    },
    handleElementDataUpdate(index, newData) {
      this.$emit('element-data-updated', { index, data: newData });
    },
    handleElementSelected(id) {
      this.$emit('element-selected', id);
    },
    handleElementDeselected(id) {
      this.$emit('element-deselected', id);
    },
    handleAutoMapping(id){
      this.$emit('auto-mapping', id);
    },
    getPreviewComponent(type) {
      return previewComponentMap[type] || DefaultPreview;
    }
  }
};
</script>

<style scoped>
.form-drop-area {
  min-height: 100%;
  transition: all 0.2s ease;
}

/* Show dashed border only when empty */
.form-drop-area:has(.empty-state) {
  min-height: 200px;
  border: 2px dashed #C4C4C4;
  border-radius: 8px;
  padding: 24px;
  background: #FAFAFA;
}

/* Show visual feedback when dragging over empty state */
.form-drop-area.drag-over {
  border: 2px dashed #004879;
  border-radius: 8px;
  background: rgba(0, 72, 121, 0.04);
  padding: 24px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  text-align: center;
}

.empty-title {
  color: #666;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  margin: 0 0 8px;
}

.empty-description {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  color: #999;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
}

/* Original ai-link styles (replaced by ResponsiveButton text type):
.ai-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #004879;
  text-decoration: underline;
}

.ai-link svg {
  width: 16px;
  height: 16px;
}
*/

.form-elements-list {
  display: flex;
  flex-direction: column;
}

.drop-zone {
  height: 8px;
  margin: 4px 0;
  position: relative;
  transition: all 0.2s ease;
}

.drop-zone--active {
  height: 32px;
  margin: 8px 0;
}

.drop-indicator {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: transparent;
  transform: translateY(-50%);
  transition: all 0.2s ease;
}

.drop-zone--active .drop-indicator {
  height: 4px;
  background: #004879;
  box-shadow: 0 0 8px rgba(0, 72, 121, 0.4);
}

.drop-zone--active .drop-indicator::before {
  content: '';
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #004879;
  box-shadow: 0 0 8px rgba(0, 72, 121, 0.4);
}

.drop-zone--active .drop-indicator::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #004879;
  box-shadow: 0 0 8px rgba(0, 72, 121, 0.4);
}

.form-element-wrapper {
  animation: fadeIn 0.2s ease;
  cursor: default;
}

.form-element-wrapper.is-dragging {
  opacity: 0.35;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.element-preview {
  flex: 1;
  min-width: 0;
}
</style>
